/**
 * auth Zod schemas — 前后端共享 wire 契约
 *
 * 字段命名严格 snake_case（按 dev-docs 50-命名规范 §1.1）
 */

import { z } from 'zod';

export const AdminRoleSchema = z.enum(['root', 'admin', 'operator', 'viewer']);
export type AdminRoleLiteral = z.infer<typeof AdminRoleSchema>;

const EmailSchema = z
  .string()
  .min(3)
  .max(255)
  .email()
  .transform((s) => s.trim().toLowerCase());

const PasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128)
  .refine((s) => /[a-zA-Z]/.test(s) && /[0-9]/.test(s), {
    message: 'Password must contain both letters and digits',
  });

const TotpCodeSchema = z.string().regex(/^[0-9]{6}$/, 'TOTP code must be 6 digits');
const BackupCodeSchema = z.string().regex(/^[0-9]{8}$/, 'Backup code must be 8 digits');

export const LoginRequestSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1).max(128),
  // 「记住设备 30 天」可信设备 token（本设备上一次 verify-2fa 勾选后客户端持有）；
  // 校验有效则跳过 TOTP 第二步直接登录
  device_token: z.string().max(256).optional(),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const Verify2FARequestSchema = z
  .object({
    code: TotpCodeSchema.optional(),
    backup_code: BackupCodeSchema.optional(),
    // 「14 天保持登录」→ refresh token TTL 延长到 14 天（默认 7 天）
    remember: z.boolean().optional(),
    // 「记住设备 30 天」→ 签发可信设备 token，下次本设备登录跳过 TOTP
    trust_device: z.boolean().optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.backup_code), {
    message: 'Provide exactly one of code or backup_code',
  });
export type Verify2FARequest = z.infer<typeof Verify2FARequestSchema>;

export const RefreshRequestSchema = z.object({
  refresh: z.string().min(1).max(256),
});
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;

export const InviteRequestSchema = z.object({
  email: EmailSchema,
  nickname: z.string().min(1).max(64),
  role: AdminRoleSchema,
});
export type InviteRequest = z.infer<typeof InviteRequestSchema>;

/**
 * 待接受邀请列表项（GET /api/v1/auth/invites，root/admin）——surface admin_invites。
 * 仅未消费（consumed_at IS NULL）的邀请；`is_expired` 由 expires_at <= now 派生（过期仍可重发）。
 * 响应**绝不**含 invite_token / token_hash（明文仅在邮件链接出现一次，重发会轮转）。
 */
export type PendingInviteResponse = {
  id: string;
  email: string;
  role: AdminRoleLiteral;
  invited_by: string;
  invited_by_nickname: string | null;
  expires_at: string;
  created_at: string;
  is_expired: boolean;
};

export const AcceptInviteRequestSchema = z.object({
  invite_token: z.string().min(1).max(256),
  password: PasswordSchema,
});
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequestSchema>;

export const BindTotpRequestSchema = z.object({
  code: TotpCodeSchema,
  // 「14 天保持登录」→ 首次绑定即发的 session 也延长 refresh TTL
  remember: z.boolean().optional(),
});
export type BindTotpRequest = z.infer<typeof BindTotpRequestSchema>;

export const PasswordResetRequestSchema = z.object({
  email: EmailSchema,
});
export type PasswordResetRequestPayload = z.infer<typeof PasswordResetRequestSchema>;

export const PasswordResetConfirmSchema = z.object({
  reset_token: z.string().min(1).max(256),
  password: PasswordSchema,
});
export type PasswordResetConfirm = z.infer<typeof PasswordResetConfirmSchema>;

/**
 * 已登录 admin 自助改密（PATCH /api/v1/me/password）。
 * current_password 仅非空校验（真伪由 service scrypt 比对存储 hash 决定，错→AUTH_INVALID_CREDENTIALS）；
 * new_password 复用 PasswordSchema 强度策略。改密成功 → bump session_version + revoke 全 session（含本会话）。
 */
export const ChangePasswordRequestSchema = z.object({
  current_password: z.string().min(1).max(256),
  new_password: PasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

/**
 * 已登录 admin 自助改资料（PATCH /api/v1/me/profile）。当前仅显示名（admin_users.nickname，
 * admin 独有表、本人语义）；非安全字段 → 不动 session。nickname 长度对齐 schema varchar(64)。
 */
export const UpdateSelfProfileRequestSchema = z.object({
  nickname: z.string().trim().min(1).max(64),
});
export type UpdateSelfProfileRequest = z.infer<typeof UpdateSelfProfileRequestSchema>;

/**
 * 已登录 admin 自助 2FA 因子管理（ADM-A9）。范围：TOTP 重绑（轮转密钥）+ 备用码重生成——均**强化**
 * 鉴权材料管理（受 requireAuth(mfa)+本人 scope+audit 保护），非降级；2FA 强制（P0 §1），故不提供「解绑/禁用」。
 * 敏感操作前以 current_password 再认证（错 → AUTH_INVALID_CREDENTIALS）。
 */

/** 重生成备用码：需 current_password 再认证。返回 10 个明文码（仅一次性展示/下载，旧码全部失效）。 */
export const RegenerateBackupCodesRequestSchema = z.object({
  current_password: z.string().min(1),
});
export type RegenerateBackupCodesRequest = z.infer<typeof RegenerateBackupCodesRequestSchema>;
export type RegenerateBackupCodesResponse = { backup_codes: string[] };

/** TOTP 重绑·开始：current_password 再认证 → 返回新 secret challenge（与 TotpBindChallenge 同形）。 */
export const RebindTotpStartRequestSchema = z.object({
  current_password: z.string().min(1),
});

/** TOTP 重绑·确认：对新 secret 出示一次 6 位 code → 轮转生效（错/过期 → AUTH_MFA_INVALID）。 */
export const RebindTotpConfirmRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/),
});
export type RebindTotpConfirmRequest = z.infer<typeof RebindTotpConfirmRequestSchema>;

/**
 * 通知偏好（GET/PATCH /api/v1/me/notification-preferences，admin 独有表 admin_notification_prefs）。
 * prefs：通知类型 key → 各渠道开关；dnd_*：勿扰时段（HH:mm 墙上时刻）。本人 scope。
 */
const NotificationChannelPrefsSchema = z.object({
  email: z.boolean(),
  in_app: z.boolean(),
  push: z.boolean(),
});
export type NotificationChannelPrefs = z.infer<typeof NotificationChannelPrefsSchema>;
const HhMmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:mm');
export const UpdateNotificationPrefsRequestSchema = z
  .object({
    prefs: z.record(z.string().min(1).max(64), NotificationChannelPrefsSchema).optional(),
    dnd_enabled: z.boolean().optional(),
    dnd_start: HhMmSchema.nullable().optional(),
    dnd_end: HhMmSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one field required' });
export type UpdateNotificationPrefsRequest = z.infer<typeof UpdateNotificationPrefsRequestSchema>;
export type NotificationPrefsResponse = {
  prefs: Record<string, NotificationChannelPrefs>;
  dnd_enabled: boolean;
  dnd_start: string | null;
  dnd_end: string | null;
};

/**
 * 登录响应（codex P1 #2 后三 stage）：
 * - `verify_2fa_required`：已绑定 TOTP，需要再调 /auth/verify-2fa
 * - `bind_totp_required`：未绑定 TOTP（accept-invite 后首登），必须调 /auth/bind-totp 完成绑定
 * - `authenticated`：完成 2FA / TOTP 绑定后才会返；带完整 access + refresh
 */
export type LoginResponse =
  | {
      stage: 'verify_2fa_required';
      verify_2fa_token: string;
      expires_in_sec: number;
    }
  | {
      stage: 'bind_totp_required';
      bind_totp_token: string;
      expires_in_sec: number;
    }
  | {
      stage: 'authenticated';
      access: string;
      refresh: string;
      user: AdminUserResponse;
      expires_in_sec: number;
      // verify-2fa 勾选「记住设备」时返回的可信设备 token（客户端持久化，下次登录携带）；
      // 其余路径（普通 login / 跳过 2FA 登录）为 null
      device_token?: string | null;
    };

export type BindTotpResponse = {
  backup_codes: string[];
  access: string;
  refresh: string;
  user: AdminUserResponse;
  expires_in_sec: number;
};

export type AdminUserResponse = {
  id: string;
  email: string;
  nickname: string;
  /** 账号 CODE（2–3 位大写字母，超管在团队成员中设置）；未设置为 null。素材署名 / 创意组名 BY 段用 */
  account_code?: string | null;
  role: AdminRoleLiteral;
  status: 'pending' | 'active' | 'disabled';
  totp_enabled: boolean;
  last_login_at: string | null;
};

export type TotpBindChallenge = {
  secret_base32: string;
  otpauth_uri: string;
};

export type AcceptInviteResponse = {
  totp_setup: TotpBindChallenge;
  user: AdminUserResponse;
};

/**
 * 可信设备（「记住此设备 30 天」）列表项 —— 当前 admin 自助管理本人设备
 *
 * 安全：响应**绝不**含 device token / token_hash（仅元信息供用户辨认并撤销）。
 * - last_used_at 为 null：从未用该设备跳过 TOTP（添加后还没在此设备登录过）
 * 字段全 snake_case，时间均 ISO 8601 with timezone。
 */
export type TrustedDeviceResponse = {
  id: string;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
};

/**
 * 登录会话（refresh session）列表项 —— 当前 admin 自助管理本人活跃会话
 * （GET /api/v1/me/sessions；DELETE /api/v1/me/sessions/:id 撤销某会话）。
 *
 * 安全：响应**绝不**含 refresh_token / token_hash（hash 泄露即可离线比对），仅元信息供辨认并撤销。
 * - last_used_at：该会话最近一次 refresh 时刻（服务端取 `admin_sessions.created_at` —— refresh 轮换
 *   新建行，故该行的 created_at 即其轮换时刻；同名列无写入点，见 novel-admin#491）。粒度 ≤ 15 分钟
 *   （access TTL），因 web 的 refresh 由请求驱动、无定时器，故它同时是「最近活跃」的准确代理。
 * - 不含 `current` 标记：access token 载荷不携带 session_id，无法从 token 反推当前会话（MVP 由用户按
 *   ua/ip/last_used_at 辨认）；如需 current 标记须先在 JWT 载荷加 session_id（另起契约，本轮不做）。
 * 字段全 snake_case，时间均 ISO 8601 with timezone。
 */
export type SelfSessionResponse = {
  id: string;
  user_agent: string | null;
  ip: string | null;
  remember: boolean;
  created_at: string;
  last_used_at: string;
  expires_at: string;
};
